import './applicantEditEmploymentPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { Input } from '../../../components/Input/Input';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import { Select } from '../../../components/Select/Select';
import { employedTypeOptions } from '../../../store/applicantStore/constants/employedTypeOptions';
import { employmentStatusOptions } from '../../../store/applicantStore/constants/employmentStatusOptions';
import { HALF_YEAR_IN_MONTHS_AS_STRING } from '../../../store/applicantStore/constants/halfYear';
import { monthsAtIncomeOptions } from '../../../store/applicantStore/constants/monthsAtIncomeOptions';
import { previousEmploymentTypeOptions } from '../../../store/applicantStore/constants/previousEmploymentTypeOptions';
import { unemployedTypeOptions } from '../../../store/applicantStore/constants/unemployedTypeOptions';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: {
      employedGrossAnnualIncome,
      reviewEditPathname,
      reviewEditProgress,
      unemployedGrossAnnualIncome,
    },
    currentIncomeEmployerName,
    currentIncomeEmploymentType,
    currentIncomeJobTitle,
    currentIncomeMonthsAt,
    isCurrentlyEmployed,
    isSyncingIncome,
    previousIncomeEmployerName,
    previousIncomeEmploymentType,
    previousIncomeGrossAnnual,
    previousIncomeJobTitle,
    syncingIncomeFailedMessage,
    setCurrentIncomeEmployerName,
    setCurrentIncomeEmploymentType,
    setCurrentIncomeGrossAnnual,
    setCurrentIncomeJobTitle,
    setCurrentIncomeMonthsAt,
    setIsCurrentlyEmployed,
    setPreviousIncomeEmployerName,
    setPreviousIncomeEmploymentType,
    setPreviousIncomeGrossAnnual,
    setPreviousIncomeJobTitle,
    syncIncome,
  } = useApplicantStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      currentEmployerNameValue: currentIncomeEmployerName,
      currentIncomeMonthsAtValue: currentIncomeMonthsAt,
      employmentTypeValue: currentIncomeEmploymentType,
      grossAnnualIncomeValue: toCurrencyString(employedGrossAnnualIncome),
      isCurrentlyEmployedValue: isCurrentlyEmployed,
      jobTitleValue: currentIncomeJobTitle,
      previousEmployerNameValue: previousIncomeEmployerName,
      previousEmploymentTypeValue: previousIncomeEmploymentType,
      previousGrossAnnualIncomeValue: toCurrencyString(
        previousIncomeGrossAnnual,
      ),
      previousJobTitleValue: previousIncomeJobTitle,
      unemploymentGrossAnnualIncomeValue: toCurrencyString(
        unemployedGrossAnnualIncome,
      ),
      unemploymentTypeValue: currentIncomeEmploymentType,
    },
    delayError: 200,
    mode: 'onTouched',
    shouldFocusError: false,
  });

  const shouldShowEmployedTypeFields =
    watch('isCurrentlyEmployedValue') === YES;

  const shouldShowPreviousEmployedTypeFields = employedTypes.includes(
    watch('previousEmploymentTypeValue') as EmploymentType,
  );

  const shouldShowPreviousEmploymentSection =
    watch('currentIncomeMonthsAtValue') === HALF_YEAR_IN_MONTHS_AS_STRING;

  const employmentStatusValidation = {
    required: t('validation.employmentStatus.required'),
  };

  const employmentTypeValidation = {
    required: t('validation.employmentType.required'),
  };

  const employerNameValidation = {
    required: t('validation.employerName.required'),
  };

  const jobTitleValidation = {
    required: t('validation.jobTitle.required'),
  };

  const monthsAtIncomeValidation = {
    required: t('validation.employmentDuration.required'),
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      currentEmployerNameValue,
      currentIncomeMonthsAtValue,
      employmentTypeValue,
      grossAnnualIncomeValue,
      isCurrentlyEmployedValue,
      jobTitleValue,
      previousEmployerNameValue,
      previousEmploymentTypeValue,
      previousGrossAnnualIncomeValue,
      previousJobTitleValue,
      unemploymentGrossAnnualIncomeValue,
      unemploymentTypeValue,
    } = values;

    if (
      !isCurrentlyEmployedValue ||
      (shouldShowEmployedTypeFields && !employmentTypeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentTypeValue) ||
      (shouldShowEmployedTypeFields && !currentEmployerNameValue) ||
      (shouldShowEmployedTypeFields && !jobTitleValue) ||
      (shouldShowEmployedTypeFields && !grossAnnualIncomeValue) ||
      (!shouldShowEmployedTypeFields && !unemploymentGrossAnnualIncomeValue) ||
      !currentIncomeMonthsAtValue
    ) {
      return;
    }

    if (
      shouldShowPreviousEmploymentSection &&
      (!previousEmploymentTypeValue ||
        (shouldShowPreviousEmployedTypeFields && !previousEmployerNameValue) ||
        (shouldShowPreviousEmployedTypeFields && !previousJobTitleValue) ||
        !previousGrossAnnualIncomeValue)
    ) {
      return;
    }

    setCurrentIncomeMonthsAt(currentIncomeMonthsAtValue);
    setIsCurrentlyEmployed(isCurrentlyEmployedValue);

    if (shouldShowEmployedTypeFields) {
      setCurrentIncomeEmployerName(currentEmployerNameValue);
      setCurrentIncomeEmploymentType(employmentTypeValue);
      setCurrentIncomeGrossAnnual(grossAnnualIncomeValue);
      setCurrentIncomeJobTitle(jobTitleValue);
    } else {
      setCurrentIncomeEmployerName(null);
      setCurrentIncomeEmploymentType(unemploymentTypeValue);
      setCurrentIncomeGrossAnnual(unemploymentGrossAnnualIncomeValue);
      setCurrentIncomeJobTitle(null);
    }

    if (shouldShowPreviousEmploymentSection) {
      setPreviousIncomeEmploymentType(previousEmploymentTypeValue);
      setPreviousIncomeGrossAnnual(previousGrossAnnualIncomeValue);
    } else {
      setPreviousIncomeEmploymentType(null);
      setPreviousIncomeGrossAnnual(null);
    }

    if (
      shouldShowPreviousEmploymentSection &&
      shouldShowPreviousEmployedTypeFields
    ) {
      setPreviousIncomeEmployerName(previousEmployerNameValue);
      setPreviousIncomeJobTitle(previousJobTitleValue);
    } else {
      setPreviousIncomeEmployerName(null);
      setPreviousIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncIncome();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page applicant-edit-employment-page">
        <Header
          progress={reviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.applicantEditEmployment.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditEmployment.form.h1}>
                {t('ApplicantEditEmploymentPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditEmployment.form.h2}>
                {t('ApplicantEditEmploymentPage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantEditEmployment.alert.error
                    }
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <Select
                    {...register(
                      'isCurrentlyEmployedValue',
                      employmentStatusValidation,
                    )}
                    error={errors.isCurrentlyEmployedValue}
                    errorTestId={
                      locator.pages.applicantEditEmployment.form
                        .employmentStatusSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditEmployment.form
                        .employmentStatusSelect
                    }
                    label={t('ApplicantEditEmploymentPage.employmentStatus')}
                    options={employmentStatusOptions}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <Select
                        {...register(
                          'employmentTypeValue',
                          employmentTypeValidation,
                        )}
                        error={errors.employmentTypeValue}
                        errorTestId={
                          locator.pages.applicantEditEmployment.form
                            .employmentTypeSelectError
                        }
                        inputTestId={
                          locator.pages.applicantEditEmployment.form
                            .employmentTypeSelect
                        }
                        label={t('ApplicantEditEmploymentPage.type')}
                        options={employedTypeOptions}
                      />
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <Select
                        {...register(
                          'unemploymentTypeValue',
                          employmentTypeValidation,
                        )}
                        error={errors.unemploymentTypeValue}
                        errorTestId={
                          locator.pages.applicantEditEmployment.form
                            .unemploymentTypeSelectError
                        }
                        inputTestId={
                          locator.pages.applicantEditEmployment.form
                            .unemploymentTypeSelect
                        }
                        label={t('ApplicantEditEmploymentPage.type')}
                        options={unemployedTypeOptions}
                      />
                    </div>
                  )}
                </div>

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register(
                        'currentEmployerNameValue',
                        employerNameValidation,
                      )}
                      data-testid={
                        locator.pages.applicantEditEmployment.form
                          .employerNameInput
                      }
                      error={errors.currentEmployerNameValue}
                      errorTestId={
                        locator.pages.applicantEditEmployment.form
                          .employerNameInputError
                      }
                      label={t('ApplicantEditEmploymentPage.employerName')}
                    />
                  </div>
                )}

                {shouldShowEmployedTypeFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Input
                      {...register('jobTitleValue', jobTitleValidation)}
                      data-testid={
                        locator.pages.applicantEditEmployment.form.jobTitleInput
                      }
                      error={errors.jobTitleValue}
                      errorTestId={
                        locator.pages.applicantEditEmployment.form
                          .jobTitleInputError
                      }
                      label={t('ApplicantEditEmploymentPage.jobTitle')}
                    />
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  {shouldShowEmployedTypeFields && (
                    <div>
                      <NumberInput
                        {...register(
                          'grossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        data-testid={
                          locator.pages.applicantEditEmployment.form
                            .grossAnnualIncomeInput
                        }
                        error={errors.grossAnnualIncomeValue}
                        errorTestId={
                          locator.pages.applicantEditEmployment.form
                            .grossAnnualIncomeInputError
                        }
                        label={t(
                          'ApplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        currency
                      />
                    </div>
                  )}

                  {!shouldShowEmployedTypeFields && (
                    <div>
                      <NumberInput
                        {...register(
                          'unemploymentGrossAnnualIncomeValue',
                          grossAnnualIncomeValidation,
                        )}
                        data-testid={
                          locator.pages.applicantEditEmployment.form
                            .unemploymentGrossAnnualIncomeInput
                        }
                        error={errors.unemploymentGrossAnnualIncomeValue}
                        errorTestId={
                          locator.pages.applicantEditEmployment.form
                            .unemploymentGrossAnnualIncomeInputError
                        }
                        label={t(
                          'ApplicantEditEmploymentPage.grossAnnualIncome',
                        )}
                        currency
                      />
                    </div>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Select
                    {...register(
                      'currentIncomeMonthsAtValue',
                      monthsAtIncomeValidation,
                    )}
                    error={errors.currentIncomeMonthsAtValue}
                    errorTestId={
                      locator.pages.applicantEditEmployment.form
                        .employmentDurationSelectError
                    }
                    inputTestId={
                      locator.pages.applicantEditEmployment.form
                        .employmentDurationSelect
                    }
                    label={t('ApplicantEditEmploymentPage.duration')}
                    options={monthsAtIncomeOptions}
                  />
                </div>
              </div>

              {shouldShowPreviousEmploymentSection && (
                <div
                  data-testid={
                    locator.pages.applicantEditEmployment.form
                      .previousEmploymentRoot
                  }
                  style={{ marginBottom: '3rem' }}
                >
                  <h3
                    data-testid={
                      locator.pages.applicantEditEmployment.form
                        .previousEmploymentH3
                    }
                  >
                    {t('ApplicantEditEmploymentPage.previousEmployment')}
                  </h3>

                  <div>
                    <Select
                      {...register(
                        'previousEmploymentTypeValue',
                        employmentTypeValidation,
                      )}
                      error={errors.previousEmploymentTypeValue}
                      errorTestId={
                        locator.pages.applicantEditEmployment.form
                          .previousEmploymentTypeSelectError
                      }
                      inputTestId={
                        locator.pages.applicantEditEmployment.form
                          .previousEmploymentTypeSelect
                      }
                      label={t('ApplicantEditEmploymentPage.type')}
                      options={previousEmploymentTypeOptions}
                    />
                  </div>

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <Input
                        {...register(
                          'previousEmployerNameValue',
                          employerNameValidation,
                        )}
                        data-testid={
                          locator.pages.applicantEditEmployment.form
                            .previousEmployerNameInput
                        }
                        error={errors.previousEmployerNameValue}
                        errorTestId={
                          locator.pages.applicantEditEmployment.form
                            .previousEmployerNameInputError
                        }
                        label={t('ApplicantEditEmploymentPage.employerName')}
                      />
                    </div>
                  )}

                  {shouldShowPreviousEmployedTypeFields && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <Input
                        {...register(
                          'previousJobTitleValue',
                          jobTitleValidation,
                        )}
                        data-testid={
                          locator.pages.applicantEditEmployment.form
                            .previousEmploymentJobTitleInput
                        }
                        error={errors.previousJobTitleValue}
                        errorTestId={
                          locator.pages.applicantEditEmployment.form
                            .previousEmploymentJobTitleInputError
                        }
                        label={t('ApplicantEditEmploymentPage.jobTitle')}
                      />
                    </div>
                  )}

                  <div style={{ marginTop: '1.5rem' }}>
                    <NumberInput
                      {...register(
                        'previousGrossAnnualIncomeValue',
                        grossAnnualIncomeValidation,
                      )}
                      data-testid={
                        locator.pages.applicantEditEmployment.form
                          .previousEmploymentGrossAnnualIncomeInput
                      }
                      error={errors.previousGrossAnnualIncomeValue}
                      errorTestId={
                        locator.pages.applicantEditEmployment.form
                          .previousEmploymentGrossAnnualIncomeInputError
                      }
                      label={t('ApplicantEditEmploymentPage.grossAnnualIncome')}
                      currency
                    />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    data-testid={
                      locator.pages.applicantEditEmployment.form.cancelButton
                    }
                    variant="secondary"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditEmploymentPage.cancel')}
                  </Button>

                  <Button
                    data-testid={
                      locator.pages.applicantEditEmployment.form.submitButton
                    }
                    disabled={!isValid}
                    loader={isSyncingIncome}
                    fullWidth
                    submit
                  >
                    {t('ApplicantEditEmploymentPage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantEditEmployment.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditEmploymentPage';
